/**
 * Shared contract for the askRichard first-run tour (Tableau movers, Oct 2026).
 *
 * Owned by the lead. Engine (TourProvider/TourOverlay), stops (tourSteps) and entry points
 * (offer card, chip, replay) import from here and never edit it. A type change happens here
 * and in every consumer in one commit.
 */

export const TOUR_VERSION = 1 as const;

/** The three stops, in order. */
export type TourStepId = 'find' | 'use' | 'make';

export const TOUR_STEP_ORDER: ReadonlyArray<TourStepId> = ['find', 'use', 'make'];

/** Where the user started the tour from. */
export type TourSource = 'offer' | 'chip' | 'guide_page' | 'url';

/** How stop 3 ended the tour. */
export type TourExit = 'copy' | 'describe' | 'finish';

/** Per-user record in localStorage. `unseen` is the absence of a record. */
export type TourRecordStatus = 'unseen' | 'dismissed' | 'completed';

export type TourRecord = {
  status: Exclude<TourRecordStatus, 'unseen'>;
  /** ISO timestamp of the last status change. */
  updatedAt: string;
  version: typeof TOUR_VERSION;
};

/**
 * A DOM target. Selectors are `[data-tour="..."]` attributes added by the entry-point lane.
 * `fallbacks` are tried in order when the primary is absent (e.g. the collapsed rail strip).
 */
export type TourTarget = {
  selector: string;
  fallbacks?: ReadonlyArray<string>;
};

/**
 * One spotlight movement inside a stop. A stop is a sequence of phases; the popover copy stays
 * the same while the spotlight walks through them.
 *
 * - `show`: spotlight the target until the user advances.
 * - `click`: spotlight the target, then perform its effect when the user advances.
 * Every movement requires a click or keyboard action. The final stop waits for an explicit exit choice.
 */
export type TourPhase =
  | {
      kind: 'show';
      target: TourTarget;
      /** Pulse the target while it is shown (stop 3: New Lens pulses with no click step). */
      pulse?: boolean;
    }
  | {
      kind: 'click';
      target: TourTarget;
      /** Named effect the engine performs on click. The engine maps names to real callbacks. */
      onClick?: TourClickEffect;
    };

/**
 * Effects a click phase can ask for. The engine owns the implementation so stop definitions
 * stay data. `navigate:library` uses the shell navigation (GET only). `demo:togglePeriod`
 * switches the static demo board between its two fixture periods.
 */
export type TourClickEffect = 'navigate:library' | 'demo:togglePeriod' | 'none';

/** A button in the stop 3 footer. Each one ends the tour and starts the real action. */
export type TourExitButton = {
  exit: TourExit;
  label: string;
  /** Visual weight. One primary at most. */
  variant: 'primary' | 'secondary';
};

export type TourStep = {
  id: TourStepId;
  title: string;
  body: string;
  /** Label for the Next button, e.g. "Next: use a report". Omitted on the last stop. */
  nextLabel?: string;
  /**
   * Spotlight walk for this stop. Empty means the popover shows with no spotlight.
   * The popover anchors to the target of the LAST phase.
   */
  phases: ReadonlyArray<TourPhase>;
  /** Whether the engine mounts the static demo board overlay during this stop. */
  overlay?: 'demoBoard';
  /** Stop 3 only: buttons that end the tour into a real action. */
  exits?: ReadonlyArray<TourExitButton>;
  /** Stop 3 only: small footer line. */
  footer?: string;
};

/** Telemetry. Category is always `askrichard_tour`. Never throws. */
export const TOUR_TELEMETRY_CATEGORY = 'askrichard_tour' as const;

export type TourEvent =
  | { action: 'offer_shown' }
  | { action: 'chip_shown' }
  | { action: 'offer_accepted'; source: TourSource }
  | { action: 'offer_dismissed'; source: 'offer' | 'chip' }
  | { action: 'step_viewed'; step: TourStepId }
  | { action: 'phase_advanced'; step: TourStepId; phase: number; source: 'next' | 'target' }
  | { action: 'step_clicked_by_user'; step: TourStepId }
  | { action: 'step_clicked_by_cursor'; step: TourStepId }
  | { action: 'skipped'; step: TourStepId; reason: 'user' | 'auto' }
  | { action: 'completed'; exit: TourExit; duration_ms: number }
  | { action: 'target_missing'; step: TourStepId; selector: string };

/** Engine state exposed through `useTour()`. */
export type TourStatus = 'idle' | 'running';

export type TourState = {
  status: TourStatus;
  /** Index into TOUR_STEP_ORDER while running. */
  stepIndex: number;
  /** Index of the active phase inside the current stop. */
  phaseIndex: number;
  source: TourSource | null;
  /** Whether the user prefers reduced motion. */
  reducedMotion: boolean;
  /** Demo board period index (0 or 1) during stop 2. */
  demoPeriodIndex: 0 | 1;
};

/**
 * Real shell actions the engine may call. index.tsx supplies them. Every one is a GET
 * navigation or a pure UI state change. None of them writes to /api/.
 */
export type TourNavigation = {
  /** Go to askRichard Home (view 'landing'). */
  goHome: () => void;
  /** Go to the Library (view 'lensIndex'). */
  goLibrary: () => void;
  /** Open the signed-in person’s Tableau migration collection after the tour. */
  goMigration?: () => void;
  /**
   * Open the real New Lens sheet with an empty brief. D3 confirms this writes nothing
   * until the user submits.
   */
  openCreateSheet: () => void;
};

/** Shell view keys the tour cares about. Mirrors `ViewKey` in viewState.ts without importing it. */
export type TourShellView =
  | 'landing'
  | 'lens'
  | 'lensIndex'
  | 'thread'
  | 'area'
  | 'catalogue'
  | 'dataGuide'
  | 'usingAskRichard'
  | 'certification';

/** Everything the provider needs from the shell. index.tsx supplies all of it. */
export type TourReportSummary = { title: string; body: string; button: string };

export type TourProviderProps = {
  /** Signed-in user email; null means auth is still loading: no record, no offer, no chip. */
  userEmail: string | null;
  /** Signed-in user id (id_member) for the Snowplow user id. Never the email. */
  userId: string | null;
  /** Current shell view. The offer needs 'landing'; the chip needs 'lens'. */
  view: TourShellView;
  /** True when the first view of this page load was 'lens' (a shared Lens link). */
  arrivedOnLens: boolean;
  /** True while the askRichard side pane is open on a Lens. The chip hides then. */
  askPaneOpen: boolean;
  /** True while sessions or Lenses are still loading on Home. The offer waits for both. */
  homeLoading: boolean;
  /** True while LensCreateSheet is open, a stream runs, a Lens streams in, or a dashboard dialog shows. */
  tourBlocked: boolean;
  /** True on narrow screens; the tour never offers or runs there. */
  isNarrow: boolean;
  /** askRichard threads the user has. null while loading or failed: no offer, no chip. */
  threadCount: number | null;
  /** Lenses the user owns (shared-with-you ones excluded). null while loading or failed: no offer, no chip. */
  ownLensCount: number | null;
  /** True when the page loaded with `?tour=1`. The provider starts the tour once ready and never persists this. */
  tourRequested: boolean;
  navigation: TourNavigation;
  /** Personalised report introduction shown at the end of the tour. */
  reportSummary?: TourReportSummary;
  children: React.ReactNode;
};

/** API returned by `useTour()`. */
export type TourApi = TourState & {
  record: TourRecordStatus;
  /** Start at stop 1. Fires offer_accepted with the source. */
  start: (source: TourSource) => void;
  /** Hide the offer and chip for good. Fires offer_dismissed. */
  dismiss: (source: 'offer' | 'chip') => void;
  next: () => void;
  back: () => void;
  /** Skip ends the tour without an exit action; record stays as it was. */
  skip: () => void;
  /** Stop 3 exits. Marks the record completed, then runs the real action. */
  exitWith: (exit: TourExit) => void;
  /** Called by the demo board when the user clicks its period control. */
  reportTargetClick: () => void;
  /** True when the Home offer card should render (all offer conditions met). */
  offerVisible: boolean;
  /** True when the shared-Lens chip should render. The Lens page still hides it in edit mode. */
  chipVisible: boolean;
  tourBlocked: boolean;
  isNarrow: boolean;
  /** Persistent copy guide after stop 3; follows Library and Lens views until dismissed or the copy dialog opens. */
  copyHintVisible: boolean;
};

/** Minimal shape the demo board wrapper receives from the engine. */
export type DemoBoardProps = {
  periodIndex: 0 | 1;
  /** The engine wires this to the period control's click. */
  onPeriodClick: () => void;
  /** Spotlight phase name the board uses to decide which menu to show open. */
  activeTarget: 'period' | 'tableMenu' | 'printMenu' | null;
};

/** Step and copy constants that other lanes read. */
export const OFFER_BODY_TABLEAU = 'Take a short tour, then check where your Tableau workbooks are moving.';

export const TOUR_COPY = {
  offerTitle: 'New to askRichard?',
  offerAccept: 'Show me',
  offerDismiss: 'Not now',
  chip: 'New here? Take the 40-second tour',
  helpLine: 'Questions? Ask in #analytics-helpline.',
  skip: 'Skip',
  back: 'Back',
  next: 'Next',
} as const;

/** `data-tour` attribute values. The entry-point lane adds these to the shell. */
export const TOUR_ANCHORS = {
  pulseSection: 'rail-pulse',
  libraryRow: 'rail-library',
  newLensRow: 'rail-new-lens',
  usingAskRichardRow: 'rail-using-askrichard',
  composer: 'home-composer',
  demoBoard: 'demo-board',
} as const;

export type TourAnchor = (typeof TOUR_ANCHORS)[keyof typeof TOUR_ANCHORS];

export function tourSelector(anchor: TourAnchor): string {
  return `[data-tour="${anchor}"]`;
}

/** `?tour=1` replays the tour from Home. Read once at mount; never persisted. */
export const TOUR_URL_PARAM = 'tour' as const;
